import { useCallback, useState } from 'react';
import { DragEndEvent, DragOverEvent, DragStartEvent } from '@dnd-kit/core';
import { TaskColumn, TaskItem } from '../../../../types/topic';
import { Types } from './taskTypes';
import { logger } from '../../../../utils/logger';

type MoveTaskFn = (id: string, toIndex: number) => void;
type MoveTaskBetweenColumnsFn = (
  id: string,
  newStatus: TaskItem['status'],
  newIndex: number
) => void;

interface UseDndHandlersProps {
  tasks: TaskItem[];
  columns: TaskColumn[];
  moveTask: MoveTaskFn;
  moveTaskBetweenColumns: MoveTaskBetweenColumnsFn;
  setDragState: (state: { columnId: string | null; index: number | null }) => void;
  setDragOverColumn: (column: string | null) => void;
  setTargetDropColumnId: (id: string | null) => void;
}

/**
 * Hook for DnD event handlers
 */
export function useDndHandlers({
  tasks,
  columns,
  moveTask,
  moveTaskBetweenColumns,
  setDragState,
  setDragOverColumn,
  setTargetDropColumnId,
}: UseDndHandlersProps) {
  // Active drag state
  const [activeTaskId, setActiveTaskId] = useState<string | null>(null);

  // Get the active task object from its ID
  const activeTask = activeTaskId ? tasks.find(task => task.id === activeTaskId) : null;

  // Handle drag start
  const handleDragStart = useCallback((event: DragStartEvent) => {
    const { active } = event;
    setActiveTaskId(active.id as string);
  }, []);

  // Handle drag over
  const handleDragOver = useCallback(
    (event: DragOverEvent) => {
      const { active, over } = event;

      if (!over) {
        return;
      }

      const activeId = active.id as string;
      const overId = over.id as string;

      // Get the active task
      const activeTask = tasks.find(t => t.id === activeId);
      if (!activeTask) {
        return;
      }

      const overData = over.data.current as {
        type?: string;
        status?: string;
        id?: string;
      };

      // Check if hovering over a column
      const isOverColumn = overData?.type === Types.COLUMN;

      // Try to identify column targets by different methods
      const isOverColumnByStatus = columns.some(c => c.status === overId);
      const isOverColumnById = columns.some(c => c.id === overId);

      // Dragging over a column - no matter how we detect it
      if (isOverColumn || isOverColumnByStatus || isOverColumnById) {
        let targetStatus;
        let targetId;

        // If detected via overData
        if (isOverColumn && overData.status) {
          targetStatus = overData.status;
          targetId = overData.id;
        }
        // If detected via column status match
        else if (isOverColumnByStatus) {
          const matchedColumn = columns.find(c => c.status === overId);
          if (matchedColumn) {
            targetStatus = matchedColumn.status;
            targetId = matchedColumn.id;
          }
        }
        // If detected via column ID match
        else if (isOverColumnById) {
          const matchedColumn = columns.find(c => c.id === overId);
          if (matchedColumn) {
            targetStatus = matchedColumn.status;
            targetId = matchedColumn.id;
          }
        }

        // If we found a column, store its information
        if (targetStatus && targetId) {
          // Store both status and ID for later use in handleDragEnd
          setDragOverColumn(targetStatus);
          setTargetDropColumnId(targetId);

          // Also store in the dragState for easy access
          setDragState({
            columnId: targetId,
            index: 0,
          });
        }
      }
    },
    [tasks, columns, setDragState, setDragOverColumn, setTargetDropColumnId]
  );

  // Handle drag end
  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;

      // Simple cleanup function
      const cleanupState = () => {
        setActiveTaskId(null);
        setDragOverColumn(null);
        setTargetDropColumnId(null);
      };

      // If there's no drop target, clean up and return
      if (!over) {
        cleanupState();
        return;
      }

      // Get active task ID and check if valid
      const activeId = active.id as string;
      const activeTask = tasks.find(t => t.id === activeId);
      if (!activeTask) {
        cleanupState();
        return;
      }

      // Get target data
      const overData = over.data.current as {
        type?: string;
        task?: TaskItem;
        status?: string;
        id?: string;
      };

      const isOverColumn = overData?.type === Types.COLUMN;

      // Strategy 1: Using the overData directly for column drops
      if (isOverColumn) {
        if (overData.status) {
          // Always use position 0 for cross-column moves
          moveTaskBetweenColumns(activeId, overData.status, 0);
          cleanupState();
          return;
        }
      }

      // Strategy 2: Try to match by overId directly to column status or ID
      // Try to match by status first (works for default columns)
      const targetColumnByStatus = columns.find(c => c.status === over.id);
      if (targetColumnByStatus) {
        // Always use position 0 for cross-column moves
        moveTaskBetweenColumns(activeId, targetColumnByStatus.status, 0);
        cleanupState();
        return;
      }

      // Try to match by column ID (works for custom columns)
      const targetColumnById = columns.find(c => c.id === over.id);
      if (targetColumnById) {
        // Always use position 0 for cross-column moves
        moveTaskBetweenColumns(activeId, targetColumnById.status, 0);
        cleanupState();
        return;
      }

      // Try to find the target column by targetDropColumnId if available
      const targetDropColumnId = over.data.current?.targetDropColumnId;
      if (targetDropColumnId) {
        const targetColumn = columns.find(c => c.id === targetDropColumnId);
        if (targetColumn && activeTask.status !== targetColumn.status) {
          // Always use position 0 for cross-column moves
          moveTaskBetweenColumns(activeId, targetColumn.status, 0);
          cleanupState();
          return;
        }
      }

      // Next try using dragOverColumn if available
      const dragOverColumn = over.data.current?.dragOverColumn;
      if (dragOverColumn) {
        const targetColumn = columns.find(c => c.status === dragOverColumn);

        if (targetColumn && activeTask.status !== targetColumn.status) {
          // Always use position 0 for cross-column moves
          moveTaskBetweenColumns(activeId, dragOverColumn, 0);
          cleanupState();
          return;
        }
      }

      // Handle dropping on another task
      const overId = over.id as string;
      const overTask = tasks.find(t => t.id === overId);

      if (overTask) {
        // Skip if we're dropping a task onto itself (no-op)
        if (activeId === overId) {
          cleanupState();
          return;
        }

        if (activeTask.status === overTask.status) {
          // Same column reordering
          // Get all tasks in this column
          const tasksInColumn = tasks
            .filter(t => t.status === activeTask.status)
            .sort((a, b) => (a.position || 0) - (b.position || 0));

          // Find indices
          const activeIndex = tasksInColumn.findIndex(t => t.id === activeId);
          const overIndex = tasksInColumn.findIndex(t => t.id === overId);

          // Only reorder if the task is actually moving to a different position
          if (activeIndex !== overIndex) {
            // Calculate the target index with special handling for different drag directions:
            // 1. When dragging downward (activeIndex < overIndex):
            //    - Use the overIndex directly because the active item has been removed from its original position
            //    - This makes the drop target calculation more intuitive for users
            // 2. When dragging upward (activeIndex > overIndex):
            //    - Use (overIndex - 1) to account for the shift that happens when the item is removed
            //    - This prevents the "off-by-one" issue that can occur in upward drags
            // 3. Always ensure the result is non-negative (Math.max(0, ...)) to prevent invalid array indices
            const targetIndex = Math.max(0, activeIndex < overIndex ? overIndex : overIndex - 1);

            logger.component('Reordering task in column', {
              taskId: activeId,
              status: activeTask.status,
              fromIndex: activeIndex,
              toIndex: targetIndex,
            });

            moveTask(activeId, targetIndex);
          }
        } else {
          // Move to a different column - always place at the top (index 0)
          // This simplifies cross-column moves by making them predictable
          moveTaskBetweenColumns(activeId, overTask.status, 0);
        }
      }

      // Clean up at the end
      cleanupState();
    },
    [tasks, columns, moveTask, moveTaskBetweenColumns, setDragOverColumn, setTargetDropColumnId]
  );

  return {
    activeTaskId,
    activeTask,
    handleDragStart,
    handleDragOver,
    handleDragEnd,
  };
}
